/** Carnet d'adresses du client (porteur, 13/10/2026 : « il faut coder le
 *  gestionnaire de contact dans app »). Les fiches viennent aussi de la case
 *  « Enregistrer dans les contacts » de l'envoi ; ici on les crée, les
 *  corrige et les supprime. */
import { useCallback, useEffect, useState } from "react";

import { api, type Contact, type FicheContact } from "../api";
import { Erreur } from "../components/ui";

const VIDE: FicheContact = {
  email: "", given_name: "", family_name: "", organisation: "", phone_e164: "", auth_method: "otp_email",
};
const sansEspaces = (t: string) => t.replace(/[\s.-]/g, "");
// Ancien contact (nom complet seul) : premier mot = prénom, le reste = nom.
const prenomNom = (c: Contact) => {
  if (c.given_name || c.family_name) return { prenom: c.given_name ?? "", nom: c.family_name ?? "" };
  const [prenom = "", ...reste] = (c.full_name ?? "").trim().split(/\s+/);
  return { prenom, nom: reste.join(" ") };
};
const telephoneValide =(t: string) => /^\+[1-9][0-9]{6,14}$/.test(sansEspaces(t));

export function Contacts() {
  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [q, setQ] = useState("");
  // null : pas de formulaire ; "nouveau" ou l'id du contact modifie.
  const [edition, setEdition] = useState<string | null>(null);
  const [fiche, setFiche] = useState<FicheContact>(VIDE);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async (recherche: string) => {
    setContacts((await api.tousLesContacts(recherche)).contacts);
  }, []);
  useEffect(() => {
    const t = window.setTimeout(() => { charger(q).catch(setErreur); }, 250);
    return () => window.clearTimeout(t);
  }, [q, charger]);

  const ouvrir = (c: Contact | null) => {
    setErreur(null);
    setEdition(c ? c.id : "nouveau");
    setFiche(c ? {
      // Ancien contact (nom complet seul) : premier mot = prenom, comme a
      // l'envoi (#158). Avant le 20/10/2026, tout allait dans « Nom ».
      email: c.email, ...(c.given_name || c.family_name
        ? { given_name: c.given_name ?? "", family_name: c.family_name ?? "" }
        : { given_name: (c.full_name ?? "").trim().split(/\s+/)[0] ?? "",
            family_name: (c.full_name ?? "").trim().split(/\s+/).slice(1).join(" ") }),
      organisation: c.organisation ?? "", phone_e164: c.phone_e164 ?? "", auth_method: c.auth_method,
    } : VIDE);
  };
  const maj = (champ: keyof FicheContact, valeur: string) => setFiche((f) => ({ ...f, [champ]: valeur }));

  const telephone = (fiche.phone_e164 ?? "").trim();
  const raison = !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fiche.email.trim()) ? "Saisissez une adresse e-mail valide."
    : telephone && !telephoneValide(telephone) ? "Téléphone au format international : +33612345678."
      : fiche.auth_method === "otp_sms" && !telephone ? "Un code par SMS demande un téléphone." : null;

  const enregistrer = async () => {
    if (raison || !edition) return;
    setEnvoi(true);
    setErreur(null);
    const corps = { ...fiche, email: fiche.email.trim(), phone_e164: telephone ? sansEspaces(telephone) : null };
    try {
      if (edition === "nouveau") await api.creerContact(corps);
      else await api.modifierContact(edition, corps);
      setEdition(null);
      await charger(q);
    } catch (e) {
      setErreur(e);
    } finally {
      setEnvoi(false);
    }
  };

  const supprimer = async (c: Contact) => {
    if (!window.confirm(`Supprimer le contact ${c.full_name || c.email} ?`)) return;
    setErreur(null);
    try {
      await api.supprimerContact(c.id);
      if (edition === c.id) setEdition(null);
      await charger(q);
    } catch (e) {
      setErreur(e);
    }
  };

  return (
    <>
      <div className="actions actions--split" style={{ margin: "0 0 1rem" }}>
        <div>
          <h1 className="page-title">Contacts</h1>
          <p className="page-lead" style={{ margin: 0 }}>
            Proposés à la frappe quand vous ajoutez un signataire, une copie ou un intervenant.
          </p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => ouvrir(null)}>+ Ajouter un contact</button>
      </div>

      {edition && (
        <div className="card stack" style={{ marginBottom: "1rem" }}>
          <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
            {edition === "nouveau" ? "Nouveau contact" : "Modifier le contact"}
          </h2>
          <Erreur erreur={erreur} />
          <div className="ligne">
            <input className="input" placeholder="Prénom" aria-label="Prénom" value={fiche.given_name ?? ""}
                   onChange={(e) => maj("given_name", e.target.value)} />
            <input className="input" placeholder="Nom" aria-label="Nom" value={fiche.family_name ?? ""}
                   onChange={(e) => maj("family_name", e.target.value)} />
          </div>
          <input className="input" type="email" placeholder="Adresse e-mail" aria-label="Adresse e-mail"
                 value={fiche.email} onChange={(e) => maj("email", e.target.value)} />
          <div className="ligne">
            <input className="input" placeholder="Organisation (facultatif)" aria-label="Organisation"
                   value={fiche.organisation ?? ""} onChange={(e) => maj("organisation", e.target.value)} />
            <input className="input" placeholder="Téléphone (+33612345678)" aria-label="Téléphone"
                   value={fiche.phone_e164 ?? ""} onChange={(e) => maj("phone_e164", e.target.value)}
                   aria-invalid={telephone !== "" && !telephoneValide(telephone) ? true : undefined} />
          </div>
          <select className="select" aria-label="Envoi du code" value={fiche.auth_method}
                  onChange={(e) => maj("auth_method", e.target.value)}>
            <option value="otp_email">Code par e-mail</option>
            <option value="otp_sms">Code par SMS (facturé)</option>
          </select>
          {raison && <p className="muted panneau__aide" style={{ margin: 0 }}>{raison}</p>}
          <div className="pied">
            <button type="button" className="btn btn--secondary" onClick={() => setEdition(null)}>Annuler</button>
            <button type="button" className="btn btn--primary" disabled={raison !== null || envoi}
                    onClick={() => void enregistrer()}>
              Enregistrer
            </button>
          </div>
        </div>
      )}

      <div className="card stack">
        {!edition && <Erreur erreur={erreur} />}
        <input className="input" type="search" placeholder="Rechercher un nom, une adresse, une organisation"
               aria-label="Rechercher un contact" value={q} onChange={(e) => setQ(e.target.value)} />
        {contacts === null ? (
          <p className="muted">Chargement…</p>
        ) : contacts.length === 0 ? (
          <p className="muted">
            {q.trim() ? "Aucun contact ne correspond." : "Aucun contact. Ajoutez-en un, ou cochez « Enregistrer dans les contacts » en envoyant un document."}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table-contacts">
              <thead>
                <tr>
                  <th>Prénom</th>
                  <th>Nom</th>
                  <th>E-mail</th>
                  <th className="col-sec">Organisation</th>
                  <th className="col-sec">Téléphone</th>
                  <th className="col-sec">Code</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td>{prenomNom(c).prenom || "—"}</td>
                  <td>{prenomNom(c).nom || "—"}</td>
                    <td className="table-contacts__email">{c.email}</td>
                    <td className="col-sec">{c.organisation || "—"}</td>
                    <td className="col-sec">{c.phone_e164 || "—"}</td>
                    <td className="col-sec">{c.auth_method === "otp_sms" ? "SMS" : "E-mail"}</td>
                    <td className="table-contacts__actions">
                      <button type="button" className="btn btn--link" onClick={() => ouvrir(c)}>Modifier</button>
                      <button type="button" className="btn btn--link" onClick={() => void supprimer(c)}>Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
